/* global chrome */

import { SocketError } from '../exceptions';

import { TcpResult } from './types';

// some promise wrappers around chrome tcp functions

const rejectIfFailure = (
  code: number,
  resolve: (d?: number) => void,
  reject: (e: SocketError) => void
) => {
  if (code < TcpResult.Success) {
    reject(new SocketError(messageForResultCode(code)));
  }
  resolve(code);
};

const tryChromeApi = <T>(fn: (r: (t?: T) => void) => void) => (
  resolve: (t?: T) => void,
  reject: (e?: Error) => void
) => {
  try {
    fn(resolve);
  } catch (error) {
    reject(error);
  }
};

export const create = (options: chrome.sockets.SocketProperties = {}): Promise<number> =>
  new Promise(
    tryChromeApi(resolve =>
      chrome.sockets.tcp.create(options, (createInfo: { socketId: number }) =>
        resolve(createInfo.socketId)
      )
    )
  );

export const close = (socketId: number) =>
  new Promise(
    tryChromeApi(resolve => {
      chrome.sockets.tcp.disconnect(socketId, resolve);
    })
  );

export const connect = (socketId: number, port: number, host: string) =>
  new Promise((resolve, reject) => {
    chrome.sockets.tcp.connect(socketId, host, port, code => {
      rejectIfFailure(code, resolve, reject);
    });
  });

export const write = (socketId: number, data: ArrayBuffer): Promise<number> =>
  new Promise((resolve, reject) => {
    chrome.sockets.tcp.send(socketId, data, eventInfo => {
      rejectIfFailure(eventInfo.resultCode, resolve, reject);
    });
  });

export function messageForResultCode(code: number) {
  switch (code) {
    case -23:
      return 'The socket is already connected.';
    case -100:
      return 'The connection was closed.';
    case -101:
      return 'The connection was reset.';
    case -102:
      return 'The connection attempt was refused.';
    case -103:
      return 'The connection timed out waiting for an acknowledgement.';
    case -104:
      return 'The connection attempt failed.';
    case -105:
      return 'The host name could not be resolved.';
    case -106:
      return 'The internet connection was lost.';
    case -108:
      return 'The provided host or port is invalid.';
    case -109:
      return 'The provided host is unreachable.';
    case -118:
      return 'The connection attempt timed out.';
    default:
      return 'Unknown socket error.';
  }
}
